import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import MainCategoriesGrid from '../../../pages/Categories/MainCategoriesGrid';
import categoriesData from '../../../pages/Categories/categories.json';
import { Category } from '../../../pages/Categories/CategoriesList';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Hero() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  // This holds the dropdown matches (root + subcategories).
  const [dropdownResults, setDropdownResults] = useState<
    Array<{
      isRoot: boolean;
      categoryKey: string;
      categoryName: string;
      subKey?: string;
      subName?: string;
    }>
  >([]);

  // Controls showing/hiding the dropdown
  const [showDropdown, setShowDropdown] = useState(false);

  const navigate = useNavigate();

  // Load all categories from JSON on mount
  useEffect(() => {
    const parsedData = JSON.parse(JSON.stringify(categoriesData)) as {
      categories: Category[];
    };
    setCategories(parsedData.categories);
  }, []);

  // Build "dropdownResults" from the searchTerm (flat list of matches)
  useEffect(() => {
    if (!searchTerm.trim()) {
      setDropdownResults([]);
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const results: Array<{
      isRoot: boolean;
      categoryKey: string;
      categoryName: string;
      subKey?: string;
      subName?: string;
    }> = [];

    categories.forEach((cat) => {
      // Does the root category name match?
      if (cat.name.toLowerCase().includes(lowerTerm)) {
        results.push({
          isRoot: true,
          categoryKey: cat.key,
          categoryName: cat.name,
        });
      }

      // Check each subcategory
      cat.subcategories?.forEach((sub) => {
        if (sub.name.toLowerCase().includes(lowerTerm)) {
          results.push({
            isRoot: false,
            categoryKey: cat.key,
            categoryName: cat.name,
            subKey: sub.key,
            subName: sub.name,
          });
        }
      });
    });

    setDropdownResults(results);
  }, [searchTerm, categories]);

  /**
   * Called when user selects either a root category or a subcategory
   * from the dropdown. We build a single query param named "selectedCategories"
   * with comma-separated values. E.g. "cleaning_services,window_washing"
   */
  const handleSelectDropdown = (
    isRoot: boolean,
    categoryKey: string,
    subKey?: string
  ) => {
    // Build an array of category keys
    const selection: string[] = [categoryKey];
    if (!isRoot && subKey) {
      // If it’s a subcategory, include both the root + sub
      selection.push(subKey);
    }

    // Build "selectedCategories" as a comma-separated string
    const paramValue = selection.join(',');
    // Example: "cleaning_services,window_washing"

    // Navigate with that single param
    navigate(`/browse-handyman?selectedCategories=${encodeURIComponent(paramValue)}`);
  };

  /**
   * If user clicks a root category card in the grid, we do something similar:
   */
  const handleSelectCategory = (cat: Category) => {
    // Just the root
    const paramValue = cat.key;
    navigate(`/browse-handyman?selectedCategories=${encodeURIComponent(paramValue)}`);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      id="hero"
      sx={(theme) => ({
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundImage:
          'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)',
        ...theme.applyStyles('dark', {
          backgroundImage:
            'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)',
        }),
      })}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <Stack
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'center', width: { xs: '100%', sm: '70%' } }}
        >
          <Typography
            variant="h1"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              fontSize: 'clamp(3rem, 10vw, 3.5rem)',
            }}
          >
            Book&nbsp;Your Next&nbsp;
            <Typography
              component="span"
              variant="h1"
              sx={(theme) => ({
                fontSize: 'inherit',
                color: 'primary.main',
                ...theme.applyStyles('dark', {
                  color: 'primary.light',
                }),
              })}
            >
              Task
            </Typography>
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              width: { sm: '100%', md: '80%' },
            }}
          >
            Discover high-quality solutions tailored to your needs. From home
            repairs to specialized services, we connect you with skilled experts
            ready to get the job done.
          </Typography>

          {/* SEARCH BOX & BUTTON */}
          <Box sx={{ position: 'relative', width: { xs: '100%', sm: '450px' } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              useFlexGap
              sx={{ pt: 3, width: '100%' }}
            >
              <InputLabel htmlFor="search-hero" sx={visuallyHidden}>
                Search for a Service
              </InputLabel>
              <TextField
                id="search-hero"
                hiddenLabel
                size="small"
                variant="outlined"
                aria-label="Search services"
                placeholder="e.g. “electrical”, “plumbing”, etc."
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => {
                  // Delay hiding so the onMouseDown in the dropdown can register
                  setTimeout(() => setShowDropdown(false), 150);
                }}
                slotProps={{
                  htmlInput: {
                    autoComplete: 'off',
                    'aria-label': 'Search services',
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{ minWidth: 'fit-content' }}
              >
                Find now
              </Button>
            </Stack>

            {/* DROPDOWN RESULTS */}
            {showDropdown && dropdownResults.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '70px',
                  left: 0,
                  width: '100%',
                  border: '1px solid #ddd',
                  bgcolor: 'background.paper',
                  zIndex: 999,
                  maxHeight: 300,
                  overflowY: 'auto',
                  boxShadow: 1,
                }}
              >
                {dropdownResults.map((item, idx) =>
                  item.isRoot ? (
                    // Root category
                    <Box
                      key={`root-${item.categoryKey}-${idx}`}
                      sx={{
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #f1f1f1',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                      onMouseDown={() =>
                        handleSelectDropdown(true, item.categoryKey)
                      }
                    >
                      {item.categoryName}
                    </Box>
                  ) : (
                    // Subcategory
                    <Box
                      key={`sub-${item.categoryKey}-${item.subKey}`}
                      sx={{
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #f1f1f1',
                        cursor: 'pointer',
                      }}
                      onMouseDown={() =>
                        handleSelectDropdown(false, item.categoryKey, item.subKey)
                      }
                    >
                      {item.subName}{' '}
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ ml: 0.5, color: 'text.secondary' }}
                      >
                        in {item.categoryName}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            )}
          </Box>

          {/* STATIC MAIN CATEGORIES (always unfiltered) */}
          <MainCategoriesGrid
            filteredCategories={categories}
            handleSelectCategory={handleSelectCategory}
          />
        </Stack>
      </Container>
    </Box>
  );
}
